import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Seo from "../components/Seo";

export default function ProgramDetails() {
  const { slug } = useParams();

  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    async function loadArticle() {
      try {
        const indexRes = await fetch("/articles/index.json");
        const indexData = await indexRes.json();

        if (!Array.isArray(indexData.items)) {
          setLoading(false);
          return;
        }

        let foundArticle = null;

        for (const path of indexData.items) {
          const res = await fetch(path);
          const data = await res.json();

          if (data.slug === slug) {
            foundArticle = data;
            break;
          }
        }

        if (mounted) {
          setArticle(foundArticle);
          setLoading(false);
        }
      } catch (err) {
        console.error("Article load error:", err);
        setLoading(false);
      }
    }

    loadArticle();

    return () => {
      mounted = false;
    };
  }, [slug]);

  const siteUrl = "https://theseus.edu.gr";

  const seoTitle = article
    ? `${article.title} – ΚΔΒΜ2 ΘΗΣΕΑΣ`
    : "Πρόγραμμα – ΚΔΒΜ2 ΘΗΣΕΑΣ";

  const seoDescription =
    article?.summary ||
    "Δείτε αναλυτικές πληροφορίες για πρόγραμμα κατάρτισης του ΚΔΒΜ2 ΘΗΣΕΑΣ.";

  const seoImage = article?.image || "/theseus.png";

  const seoUrl = `${siteUrl}/programs/${slug}`;

  const programSchema = article
    ? {
        "@context": "https://schema.org",
        "@type": "Course",
        name: article.title,
        description: article.summary || "",
        provider: {
          "@type": "EducationalOrganization",
          name: "ΚΔΒΜ2 ΘΗΣΕΑΣ",
          url: siteUrl,
        },
        image: `${siteUrl}${seoImage}`,
        url: seoUrl,
        inLanguage: "el-GR",
      }
    : null;

  if (loading) {
    return (
      <>
        <Seo
          title="Φόρτωση προγράμματος – ΚΔΒΜ2 ΘΗΣΕΑΣ"
          description="Φόρτωση πληροφοριών προγράμματος."
          url={seoUrl}
          image="/theseus.png"
          noindex
        />

        <div className="min-h-screen flex items-center justify-center">
          <p className="text-gray-600 text-lg">Φόρτωση...</p>
        </div>
      </>
    );
  }

  if (!article) {
    return (
      <>
        <Seo
          title="Το πρόγραμμα δεν βρέθηκε – ΚΔΒΜ2 ΘΗΣΕΑΣ"
          description="Το πρόγραμμα που αναζητάτε δεν βρέθηκε."
          url={seoUrl}
          image="/theseus.png"
          noindex
        />

        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-3xl font-extrabold text-gray-900">
              Το πρόγραμμα δεν βρέθηκε
            </h1>

            <Link
              to="/"
              className="mt-6 inline-block rounded-xl bg-gray-900 px-5 py-3 text-white font-bold"
            >
              Επιστροφή στην αρχική
            </Link>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Seo
        title={seoTitle}
        description={seoDescription}
        keywords={`${article.title}, voucher ΔΥΠΑ, πρόγραμμα κατάρτισης`}
        url={seoUrl}
        image={seoImage}
        type="article"
        structuredData={programSchema}
      />

      <main className="bg-white min-h-screen">
 <section className="max-w-6xl mx-auto px-4 py-10">

  <nav className="text-sm text-gray-500 mb-6">
    <Link to="/" className="hover:underline">
      Αρχική
    </Link>
    <span className="mx-2">/</span>
    <span className="text-gray-900">{article.title}</span>
  </nav>

  {article.image && (
    <img
      src={article.image}
      alt={article.title}
      className="w-full rounded-2xl mb-10 border"
    />
  )}

  <div className="grid md:grid-cols-3 gap-10">

    {/* MAIN CONTENT */}
    <div className="md:col-span-2">

      <h1 className="text-3xl md:text-4xl font-extrabold text-gray-900">
        {article.title}
      </h1>

      {article.summary && (
        <p className="mt-4 text-lg text-gray-700">
          {article.summary}
        </p>
      )}

      {article.sections?.length > 0 && (
        <div className="mt-10 space-y-8">
          {article.sections.map((section, i) => (
            <section key={i} className="rounded-2xl border p-6">
              <h2 className="text-xl font-extrabold">
                {section.heading}
              </h2>

              <div className="mt-4 text-gray-700 whitespace-pre-line">
                {section.content}
              </div>
            </section>
          ))}
        </div>
      )}

    </div>

    {/* SIDEBAR */}
    <aside className="space-y-6">

      <div className="border rounded-2xl p-6 bg-gray-50">
        <h3 className="font-bold text-lg mb-2">
          Ενδιαφέρεστε για το πρόγραμμα;
        </h3>

        <p className="text-gray-600 text-sm">
          Επικοινωνήστε μαζί μας για πληροφορίες ή αίτηση συμμετοχής.
        </p>

        <Link
          to="/contact"
          className="block mt-4 text-center bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 rounded-xl"
        >
          Επικοινωνία
        </Link>

        {article.cta?.label && /^https?:\/\//i.test(article.cta.href || "") && (
          <a
            href={article.cta.href}
            target="_blank"
            rel="noopener noreferrer"
            className="block mt-3 text-center bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 rounded-xl"
          >
            {article.cta.label}
          </a>
        )}
      </div>

      <div className="border rounded-2xl p-6">
        <h4 className="font-bold mb-2">Τηλέφωνο</h4>
        <p className="text-gray-700">+30 2510 600205</p>

        <h4 className="font-bold mt-4 mb-2">Email</h4>
        <p className="text-gray-700">info@theseus.edu.gr</p>
      </div>

    </aside>

  </div>

</section>
      </main>
    </>
  );
}
